import { useState } from 'react';
import { Link, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';

export default function RolesCreate({ permissionGroups }) {
    const [expandedGroups, setExpandedGroups] = useState(Object.keys(permissionGroups));

    const { data, setData, post, processing, errors } = useForm({
        name: '',
        permissions: [],
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        post('/roles');
    };

    const toggleGroup = (group) => {
        setExpandedGroups(prev =>
            prev.includes(group)
                ? prev.filter(g => g !== group)
                : [...prev, group]
        );
    };

    const handlePermissionChange = (permission) => {
        const newPermissions = data.permissions.includes(permission)
            ? data.permissions.filter(p => p !== permission)
            : [...data.permissions, permission];
        setData('permissions', newPermissions);
    };

    const handleGroupSelectAll = (group, permissions) => {
        const allSelected = permissions.every(p => data.permissions.includes(p));
        if (allSelected) {
            setData('permissions', data.permissions.filter(p => !permissions.includes(p)));
        } else {
            const newPermissions = [...new Set([...data.permissions, ...permissions])];
            setData('permissions', newPermissions);
        }
    };

    const getGroupIcon = (group) => {
        const icons = {
            users: 'bi-people',
            roles: 'bi-shield',
            contacts: 'bi-person-lines-fill',
            companies: 'bi-building',
            deals: 'bi-graph-up-arrow',
            tasks: 'bi-check2-square',
            meetings: 'bi-calendar-event',
            bookings: 'bi-journal-bookmark',
            itineraries: 'bi-map',
            packages: 'bi-box',
            suppliers: 'bi-truck',
            destinations: 'bi-geo-alt',
            quotations: 'bi-file-text',
            reports: 'bi-bar-chart',
            settings: 'bi-gear',
        };
        return icons[group] || 'bi-key';
    };

    const formatGroupName = (group) => {
        return group.charAt(0).toUpperCase() + group.slice(1);
    };

    const formatPermissionAction = (permission, group) => {
        return permission.replace(` ${group}`, '').charAt(0).toUpperCase() +
            permission.replace(` ${group}`, '').slice(1);
    };

    return (
        <AppLayout title="Create Role">
            <PageHeader
                title="Create Role"
                subtitle="Define a new role with specific permissions"
                breadcrumbs={[
                    { label: 'Settings' },
                    { label: 'Roles', href: '/roles' },
                    { label: 'Create' },
                ]}
            />

            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-lg-4">
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Role Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">Role Name <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                        value={data.name}
                                        onChange={(e) => setData('name', e.target.value)}
                                        placeholder="e.g., Sales Manager"
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Selected Permissions</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge badge-primary fs-6">
                                            {data.permissions.length}
                                        </span>
                                        <span className="text-muted">permissions selected</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Quick Actions</h5>
                            </div>
                            <div className="card-body">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary w-100 mb-2"
                                    onClick={() => {
                                        const allPermissions = Object.values(permissionGroups).flat();
                                        setData('permissions', allPermissions);
                                    }}
                                >
                                    <i className="bi bi-check-all me-2"></i>
                                    Select All Permissions
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary w-100"
                                    onClick={() => setData('permissions', [])}
                                >
                                    <i className="bi bi-x-lg me-2"></i>
                                    Clear All Permissions
                                </button>
                            </div>
                        </div>

                        <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary flex-grow-1" disabled={processing}>
                                {processing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-lg me-1"></i>
                                        Create Role
                                    </>
                                )}
                            </button>
                            <Link href="/roles" className="btn btn-outline-secondary">
                                Cancel
                            </Link>
                        </div>
                    </div>

                    <div className="col-lg-8">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Permissions</h5>
                            </div>
                            <div className="card-body p-0">
                                {errors.permissions && (
                                    <div className="alert alert-danger m-3 mb-0">{errors.permissions}</div>
                                )}
                                <div className="accordion accordion-flush" id="permissionsAccordion">
                                    {Object.entries(permissionGroups).map(([group, permissions]) => {
                                        const isExpanded = expandedGroups.includes(group);
                                        const selectedCount = permissions.filter(p => data.permissions.includes(p)).length;
                                        const allSelected = permissions.length === selectedCount;

                                        return (
                                            <div className="accordion-item" key={group}>
                                                <h2 className="accordion-header">
                                                    <button
                                                        className={`accordion-button ${!isExpanded ? 'collapsed' : ''}`}
                                                        type="button"
                                                        onClick={() => toggleGroup(group)}
                                                    >
                                                        <div className="d-flex align-items-center gap-3 w-100 me-3">
                                                            <i className={`bi ${getGroupIcon(group)} fs-5`}></i>
                                                            <span className="fw-semibold">{formatGroupName(group)}</span>
                                                            <span className={`badge ms-auto ${selectedCount > 0 ? 'badge-primary' : 'bg-light text-muted'}`}>
                                                                {selectedCount}/{permissions.length}
                                                            </span>
                                                        </div>
                                                    </button>
                                                </h2>
                                                <div className={`accordion-collapse collapse ${isExpanded ? 'show' : ''}`}>
                                                    <div className="accordion-body">
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <small className="text-muted">
                                                                Manage {group} module permissions
                                                            </small>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={() => handleGroupSelectAll(group, permissions)}
                                                            >
                                                                {allSelected ? 'Deselect All' : 'Select All'}
                                                            </button>
                                                        </div>
                                                        <div className="row g-2">
                                                            {permissions.map((permission) => (
                                                                <div key={permission} className="col-md-6 col-lg-4">
                                                                    <div
                                                                        className={`card h-100 cursor-pointer ${data.permissions.includes(permission) ? 'border-primary bg-primary-subtle' : ''}`}
                                                                        onClick={() => handlePermissionChange(permission)}
                                                                    >
                                                                        <div className="card-body py-2 px-3">
                                                                            <div className="form-check mb-0">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    className="form-check-input"
                                                                                    checked={data.permissions.includes(permission)}
                                                                                    onChange={() => handlePermissionChange(permission)}
                                                                                />
                                                                                <label className="form-check-label small">
                                                                                    {formatPermissionAction(permission, group)}
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </AppLayout>
    );
}
